import styles from './Toaster.module.css';

export type ToastTone = 'success' | 'info' | 'warning' | 'error';

export interface ToastItem {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
  /** Set while the toast plays its exit animation. */
  leaving?: boolean;
}

export interface ToasterProps {
  toasts: readonly ToastItem[];
  onDismiss: (id: number) => void;
}

const GLYPH: Record<ToastTone, string> = {
  success: '✓',
  info: 'i',
  warning: '!',
  error: '!',
};

/**
 * The stack of transient notices, top-centre, newest last. A click anywhere on
 * a toast dismisses it; the × is there for keyboard users. Announced politely,
 * errors assertively.
 */
export function Toaster({ toasts, onDismiss }: ToasterProps) {
  return (
    <div className={styles.region} aria-label="Notifications">
      {toasts.map((toast) => (
        <div
          className={`${styles.toast} ${styles[toast.tone]} ${toast.leaving ? styles.leaving : ''}`}
          key={toast.id}
          onClick={() => onDismiss(toast.id)}
          role={toast.tone === 'error' ? 'alert' : 'status'}
        >
          <span className={styles.glyph} aria-hidden="true">
            {GLYPH[toast.tone]}
          </span>
          <span className={styles.text}>
            <strong className={styles.title}>{toast.title}</strong>
            {toast.description ? (
              <span className={styles.description}>{toast.description}</span>
            ) : null}
          </span>
          <button
            aria-label="Dismiss notification"
            className={styles.dismiss}
            onClick={(event) => {
              event.stopPropagation();
              onDismiss(toast.id);
            }}
            type="button"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
